import { Alert, Box, Skeleton, Stack } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addTransHeaderExpenseBundle,
  createTransHeaderExpense,
  getExpenseApplicationDetails,
} from "src/apis/controllers/expenses";
import { getFiles } from "src/apis/controllers/files";
import { FileProvider, useFileContext } from "src/context/FilesV2";
import { useLoading } from "src/hooks/useLoading";
import {
  deleteBundle,
  deleteFile,
  downloadFile,
  formatFiles,
  uploadFiles,
} from "src/util";
import {
  buildTransHeaderExpensePayload,
  getResponseMessage,
  getResponsePayload,
  getTransHeaderIdFromResponse,
  isErrorResponse,
} from "../ExpenseTransactionDetails/headerPayload";
import ExpenseHeaderDialog from "../ExpenseTransactionDetails/ExpenseHeaderDialog";
import ExpenseTransactionsExplorer from "./ExpenseTransactionsExplorer";
import MhdExpensesHeader from "./MhdExpensesHeader";

const EXPENSE_ATTACHMENTS_PROJECT_TABLE_ID = 18;

const getAttachmentRequest = (transHeaderId) => ({
  DataKey: "MHD_TransHeader_Expenses",
  DataValue: null,
  RecordId: null,
  RecordGuid: transHeaderId,
  ProjectTableId: EXPENSE_ATTACHMENTS_PROJECT_TABLE_ID,
});

const uniqueBundleIds = (bundleIds) => [
  ...new Set(bundleIds.filter((bundleId) => !!bundleId)),
];

const getBundleIdsFromUploadResponse = (res) => {
  const payload = getResponsePayload(res);

  if (Array.isArray(payload)) {
    return uniqueBundleIds(
      payload.flatMap((item) => [
        item?.bundleId,
        item?.BundleId,
        ...(Array.isArray(item?.bundleIds) ? item.bundleIds : []),
        ...(Array.isArray(item?.BundleIds) ? item.BundleIds : []),
      ]),
    );
  }

  return uniqueBundleIds([
    ...(Array.isArray(payload?.bundleIds) ? payload.bundleIds : []),
    ...(Array.isArray(payload?.BundleIds) ? payload.BundleIds : []),
  ]);
};

const getUnsavedBundleIds = (fileGroups = []) =>
  uniqueBundleIds(
    fileGroups.reduce((acc, group) => {
      const groupBundleIds =
        group.bundles
          ?.filter(
            (bundle) =>
              bundle?.files?.length > 0 &&
              (!bundle?.saved ||
                bundle.files.some(
                  (file) => !file?.dbId && file?.saved !== true,
                )),
          )
          .map((bundle) => bundle.id) ?? [];

      return [...acc, ...groupBundleIds];
    }, []),
  );

const MhdExpensesDetailsContent = () => {
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const { setLoading } = useLoading();
  const { control: filesControl, fileGroups } = useFileContext();
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isExpenseHeaderDialogOpen, setIsExpenseHeaderDialogOpen] =
    useState(false);
  const canAddExpense = applicationDetails?.settings?.canAdd === true;

  useEffect(() => {
    let isActive = true;

    const fetchApplicationDetails = async () => {
      setHasLoaded(false);
      setErrorMessage(null);
      setApplicationDetails(null);
      setLoading(true);

      try {
        if (!applicationId) {
          setErrorMessage("Δεν βρέθηκαν στοιχεία για τη ΜΗΔ.");
          return;
        }

        const res = await getExpenseApplicationDetails(applicationId);
        if (!isActive) return;

        if (res?.status === "error" || !res?.data) {
          setErrorMessage(res?.message ?? "Δεν βρέθηκαν στοιχεία για τη ΜΗΔ.");
          return;
        }

        setApplicationDetails(res.data);
      } catch (error) {
        if (!isActive) return;

        setErrorMessage(
          error?.message ?? "Προέκυψε σφάλμα κατά τη φόρτωση των στοιχείων."
        );
      } finally {
        if (isActive) {
          setHasLoaded(true);
          setLoading(false);
        }
      }
    };

    fetchApplicationDetails();

    return () => {
      isActive = false;
    };
  }, [applicationId, setLoading]);

  useEffect(() => {
    if (!isExpenseHeaderDialogOpen) {
      filesControl.initialize([]);
      filesControl.setIsDirty?.(false);
      return undefined;
    }

    let isActive = true;

    const fetchCreateAttachmentTemplates = async () => {
      try {
        const filesRes = await getFiles(getAttachmentRequest(null));
        if (!isActive) return;

        filesControl.initialize(formatFiles(filesRes?.data ?? []));
        filesControl.setIsDirty?.(false);
      } catch (error) {
        if (!isActive) return;

        filesControl.initialize([]);
        filesControl.setIsDirty?.(false);
        enqueueSnackbar(
          error?.message ?? "Δεν φορτώθηκαν τα συνημμένα.",
          { variant: "warning" },
        );
      }
    };

    fetchCreateAttachmentTemplates();

    return () => {
      isActive = false;
    };
  }, [isExpenseHeaderDialogOpen]);

  const handleAddExpense = useCallback(() => {
    if (!canAddExpense || isExpenseHeaderDialogOpen) return;

    setIsExpenseHeaderDialogOpen(true);
  }, [canAddExpense, isExpenseHeaderDialogOpen]);

  useEffect(() => {
    const isTypingTarget = (target) => {
      const tagName = target?.tagName;

      return (
        target?.isContentEditable ||
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT"
      );
    };

    const hasOpenOverlay = () =>
      document.querySelector(
        '[role="dialog"], .MuiPopover-root, .MuiMenu-root, .react-datepicker'
      );

    const handleKeyDown = (event) => {
      if (
        !event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.code !== "KeyN"
      ) {
        return;
      }

      if (isTypingTarget(event.target) || hasOpenOverlay()) return;

      if (!canAddExpense || isExpenseHeaderDialogOpen) return;

      event.preventDefault();
      handleAddExpense();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [canAddExpense, handleAddExpense, isExpenseHeaderDialogOpen]);

  const handleCancelExpenseHeader = () => {
    setIsExpenseHeaderDialogOpen(false);
  };

  const saveCreateAttachments = async (targetTransHeaderId) => {
    if (!targetTransHeaderId) return false;

    const unsavedBundleIds = getUnsavedBundleIds(fileGroups);
    const res = await filesControl.uploadFileGroups();

    if (isErrorResponse(res)) {
      enqueueSnackbar(
        getResponseMessage(res, "Δεν αποθηκεύτηκαν τα συνημμένα."),
        { variant: "error" }
      );
      return false;
    }

    const uploadedBundleIds = getBundleIdsFromUploadResponse(res);
    const bundleIds =
      uploadedBundleIds.length > 0 ? uploadedBundleIds : unsavedBundleIds;

    for (const bundleId of bundleIds) {
      const bundleRes = await addTransHeaderExpenseBundle({
        transHeaderId: targetTransHeaderId,
        bundleId,
      });

      if (isErrorResponse(bundleRes)) {
        enqueueSnackbar(
          getResponseMessage(
            bundleRes,
            "Δεν συνδέθηκε το συνημμένο με τη δαπάνη."
          ),
          { variant: "error" }
        );
        return false;
      }
    }

    filesControl.setIsDirty?.(false);
    return true;
  };

  const handleSaveExpenseHeader = async (formValues) => {
    try {
      setLoading(true);

      const res = await createTransHeaderExpense(
        buildTransHeaderExpensePayload({ formValues, applicationId })
      );

      if (isErrorResponse(res)) {
        enqueueSnackbar(
          getResponseMessage(res, "Δεν ήταν δυνατή η δημιουργία δαπάνης."),
          { variant: "error" }
        );
        return;
      }

      const newTransHeaderId = getTransHeaderIdFromResponse(res);

      if (!newTransHeaderId) {
        enqueueSnackbar("Δεν επιστράφηκε αναγνωριστικό δαπάνης.", {
          variant: "error",
        });
        return;
      }

      const shouldSaveCreateAttachments =
        filesControl.isDirty || getUnsavedBundleIds(fileGroups).length > 0;
      let attachmentsSaved = true;

      if (shouldSaveCreateAttachments) {
        attachmentsSaved = await saveCreateAttachments(newTransHeaderId);
      }

      enqueueSnackbar(
        attachmentsSaved
          ? "Η δαπάνη δημιουργήθηκε επιτυχώς."
          : "Η δαπάνη δημιουργήθηκε, αλλά δεν αποθηκεύτηκαν τα συνημμένα.",
        { variant: attachmentsSaved ? "success" : "warning" }
      );
      setIsExpenseHeaderDialogOpen(false);
      navigate(`/expenses/${applicationId}/transactions/${newTransHeaderId}`, {
        state: { openExpenseDetailDialog: true },
      });
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά τη δημιουργία δαπάνης.",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (!hasLoaded) {
      return (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={180} />
          <Skeleton variant="rounded" height={120} />
        </Stack>
      );
    }

    if (errorMessage) {
      return <Alert severity="error">{errorMessage}</Alert>;
    }

    return (
      <>
        <MhdExpensesHeader
          applicationDetails={applicationDetails}
          onAddExpense={handleAddExpense}
        />
        <Box sx={{}}>
          <ExpenseTransactionsExplorer applicationId={applicationId} />
        </Box>
        <ExpenseHeaderDialog
          open={isExpenseHeaderDialogOpen}
          mhdName={applicationDetails?.name ?? applicationDetails?.mhdName}
          isCreateMode
          onCancel={handleCancelExpenseHeader}
          onSave={handleSaveExpenseHeader}
        />
      </>
    );
  };

  return (
    <Box
      sx={{
        m: 2.5,
        p: 2.5,
        borderRadius: 1,
        backgroundColor: "#f3f3f3",
      }}
    >
      {renderContent()}
    </Box>
  );
};

const MhdExpensesDetails = () => (
  <FileProvider
    downloadFile={(fileId, type) => downloadFile(fileId, type)}
    uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
    onDeleteBundle={async (bundleId) =>
      await deleteBundle(
        bundleId,
        EXPENSE_ATTACHMENTS_PROJECT_TABLE_ID,
        null,
        null,
        null,
      )
    }
    onDeleteFile={async (fileId) => {
      await deleteFile(
        fileId,
        EXPENSE_ATTACHMENTS_PROJECT_TABLE_ID,
        null,
        null,
        null,
      );
    }}
  >
    <MhdExpensesDetailsContent />
  </FileProvider>
);

export default MhdExpensesDetails;
